













































































































import {
  defineComponent,
  reactive,
  computed,
  ref,
  watch,
} from '@vue/composition-api';
import type { Ref } from '@vue/composition-api';
import { useMutation, useQuery } from '@vue/apollo-composable';

import type { Area } from '@/useDeliveryAreaCode';
import type { ID } from '@/types';
import { toCommaFormat } from '@/utils';

import CommonProductTypeSelect from './CommonProductTypeSelect.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonUnitSelector from '@/components/Delivery/CommonUnitSelector.vue';

import getAllAreaCodes from '@/gql/areaCode/getAllAreaCodes.gql';
import createProduct from '@/gql/product/createProduct.gql';
import updateProduct from '@/gql/product/updateProduct.gql';
import getProduct from '@/gql/product/getProduct.gql';

interface ProductArea {
  id: ID;
  price: number | string;
  area: Area;
}

interface CreateProductPriceInput { areaId: ID; price: number | string; type: 'WEIGHT' | 'VOLUME' | 'PRODUCT' }
interface UpdateProductPriceInput { id: ID; price: number | string }

const CommonFormWithProductCreator = defineComponent({
  components: {
    CommonProductTypeSelect,
    CommonModalWithPreset,
    CommonUnitSelector,
  },
  props: {
    edit: {
      default() { return false; },
    },
    id: { type: [Number, String], default: -1 },
  },
  setup(props: any) {
    const product = reactive({
      productTypeId: '',
      code: '',
      refCode: '',
      productName: '',
      unit: '',
      weight: '0',
      width: '0',
      length: '0',
      height: '0',
      productId: undefined,
    });
    const temporaryAreaPriceStorage: { areaPriceByWeight: ProductArea[]; areaPriceByVolume: ProductArea[]; areaPriceByProduct: ProductArea[] } = reactive({
      areaPriceByWeight: [],
      areaPriceByVolume: [],
      areaPriceByProduct: [],
    });
    const areaPriceByWeight: Ref<ProductArea[]> = ref([]);
    const areaPriceByVolume: Ref<ProductArea[]> = ref([]);
    const areaPriceByProduct: Ref<ProductArea[]> = ref([]);

    function compareProductArea(a: ProductArea, b: ProductArea) {
      return a.area.code.localeCompare(b.area.code);
    }

    // Gather all of area code
    const { result: getAreasDetails, loading } = useQuery(
      getAllAreaCodes,
      () => ({ filter: { column: 'ACTIVE', value: true } }),
      () => ({
        // this query will enable only when productTypeId is represent, or this component render in creation mode;
        enabled: product.productId !== undefined || props.id === -1,
      }),
    );
    const getAreasCode = computed(() => getAreasDetails.value?.areas.filter((item: any) => item.active === true) || []);
    // watch, if gather area code successfully, then set area price on each area
    watch(() => getAreasCode.value, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        // Create empty area price for each area
        const mappedAreaIntoProductArea = [...getAreasCode.value.map((area: Area) => ({
          id: -1,
          price: 0,
          area,
        })).sort(compareProductArea)];

        // stringify and parse to make a deep copy
        const arrayStr = JSON.stringify(mappedAreaIntoProductArea);

        areaPriceByProduct.value = JSON.parse(arrayStr);
        areaPriceByWeight.value = JSON.parse(arrayStr);
        areaPriceByVolume.value = JSON.parse(arrayStr);

        temporaryAreaPriceStorage.areaPriceByProduct.forEach((tempAreaPrice: ProductArea) => {
          for (let i = 0; i < areaPriceByProduct.value.length; i += 1) {
            if (areaPriceByProduct.value[i].area.code === tempAreaPrice.area.code) {
              areaPriceByProduct.value[i] = { ...areaPriceByProduct.value[i], id: tempAreaPrice.id, price: tempAreaPrice.price };
            }
          }
        });
        temporaryAreaPriceStorage.areaPriceByWeight.forEach((tempAreaPrice: ProductArea) => {
          for (let i = 0; i < areaPriceByWeight.value.length; i += 1) {
            if (areaPriceByWeight.value[i].area.code === tempAreaPrice.area.code) {
              areaPriceByWeight.value[i] = { ...areaPriceByWeight.value[i], id: tempAreaPrice.id, price: tempAreaPrice.price };
            }
          }
        });
        temporaryAreaPriceStorage.areaPriceByVolume.forEach((tempAreaPrice: ProductArea) => {
          for (let i = 0; i < areaPriceByVolume.value.length; i += 1) {
            if (areaPriceByVolume.value[i].area.code === tempAreaPrice.area.code) {
              areaPriceByVolume.value[i] = { ...areaPriceByVolume.value[i], id: tempAreaPrice.id, price: tempAreaPrice.price };
            }
          }
        });
      }
    });

    // This compute property use to format value into api payload;
    const computedAreaPriceByWeight = computed(() => {
      const create: CreateProductPriceInput[] = [];
      const update: UpdateProductPriceInput[] = [];
      for (let i = 0; i < areaPriceByWeight.value.length; i += 1) {
        const { id, area, price } = areaPriceByWeight.value[i];
        const parsedPrice = parseFloat(price as string);
        if (id === -1) {
          create.push({
            areaId: area.id,
            price: parsedPrice,
            type: 'WEIGHT',
          });
        } else {
          update.push({ id, price: parsedPrice });
        }
      }
      for (let i = 0; i < areaPriceByVolume.value.length; i += 1) {
        const { id, area, price } = areaPriceByVolume.value[i];
        const parsedPrice = parseFloat(price as string);
        if (id === -1) {
          create.push({
            areaId: area.id,
            price: parsedPrice,
            type: 'VOLUME',
          });
        } else {
          update.push({ id, price: parsedPrice });
        }
      }
      for (let i = 0; i < areaPriceByProduct.value.length; i += 1) {
        const { id, area, price } = areaPriceByProduct.value[i];
        const parsedPrice = parseFloat(price as string);
        if (id === -1) {
          create.push({
            areaId: area.id,
            price: parsedPrice,
            type: 'PRODUCT',
          });
        } else {
          update.push({ id, price: parsedPrice });
        }
      }

      return { create, update };
    });
    // ----------------------------------------------------------------

    const { result: getProductDetail, loading: productDetailLoading } = useQuery(
      getProduct,
      computed(() => ({ id: props.id })),
      () => ({
        enabled: props.id !== '-1',
        fetchPolicy: 'network-only',
      }),
    );

    watch(() => getProductDetail.value, (newVal, oldVal) => {
      if (getProductDetail?.value?.product && !productDetailLoading.value) {
        const { product: queryProduct } = getProductDetail?.value;
        product.productId = queryProduct.id;
        product.productTypeId = queryProduct.productGroup;
        product.code = queryProduct.code;
        product.refCode = queryProduct.refCode;
        product.productName = queryProduct.name;
        product.unit = queryProduct.unit;
        product.weight = queryProduct.weightPerUnit;
        product.width = queryProduct.width;
        product.length = queryProduct.length;
        product.height = queryProduct.height;

        // Store Area price on temporary array, then assign to model value later;
        temporaryAreaPriceStorage.areaPriceByProduct = [...queryProduct.areaPriceByProduct].sort(compareProductArea);
        temporaryAreaPriceStorage.areaPriceByWeight = [...queryProduct.areaPriceByWeight].sort(compareProductArea);
        temporaryAreaPriceStorage.areaPriceByVolume = [...queryProduct.areaPriceByVolume].sort(compareProductArea);
      }
    });

    const computedProductInput = computed(() => {
      const vars = {
        code: product.code,
        refCode: product.refCode,
        name: product.productName,
        unit: product.unit,
        weightPerUnit: parseFloat(product.weight),
        deliveryFeePerUnit: 0,
        width: parseFloat(product.width),
        length: parseFloat(product.length),
        height: parseFloat(product.height),
        productGroup: {
          connect: '',
        },
      };
      if (product.productTypeId) vars.productGroup.connect = (product.productTypeId as any)?.id || undefined;

      return vars;
    });

    const refetchQueries = ['getProducts'];
    const createProductMutation = useMutation(createProduct, () => ({
      variables: {
        data: {
          ...computedProductInput.value,
          areaPrice: {
            create: computedAreaPriceByWeight.value.create,
          },
        },
      },
      refetchQueries,
    }));
    const updateProductMutation = useMutation(updateProduct, () => ({
      variables: {
        data: {
          ...computedProductInput.value,
          id: product.productId,
          areaPrice: {
            create: computedAreaPriceByWeight.value.create.length > 0 ? computedAreaPriceByWeight.value.create : undefined,
            update: computedAreaPriceByWeight.value.update.length > 0 ? computedAreaPriceByWeight.value.update : undefined,
          },
        },
      },
      refetchQueries,
    }));

    const computedMutation = computed(() => (
      props.edit ? updateProductMutation : createProductMutation
    ));

    const confirmModal = ref(null);
    const errorModal = ref(null);
    createProductMutation.onDone(() => {
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'รายการเพิ่มสินค้า สำเร็จ' });
    });
    updateProductMutation.onDone(() => {
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'รายการแก้ไขสินค้า สำเร็จ' });
    });

    createProductMutation.onError(() => {
      (errorModal.value as any).compose();
    });
    updateProductMutation.onError(() => {
      (errorModal.value as any).compose();
    });

    // watch(() => getAreasDetails.value, (newVal, oldVal) => {
    //   if (newVal !== oldVal) {
    //     console.log(props.id);
    //   }
    // });
    return {
      product,

      createProductMutation,
      computedMutation,
      refetchQueries,

      getAreasDetails,
      getAreasCode,
      getProduct,
      createProduct,
      confirmModal,
      errorModal,

      areaPriceByWeight,
      areaPriceByVolume,
      areaPriceByProduct,

    };
  },
});

export default CommonFormWithProductCreator;
